/**
 * What the Subskrypcja tab offers, read off `GET /me/plans`.
 *
 * ── No price is ever written into this app ──────────────────────────────────────────────────
 * Subscription requirement R6: the gross price is the payment provider's and is read from it at
 * display time. Nothing here carries a number — this module turns the catalogue's minor units into
 * the words the cards draw, and a fixture supplies the units in every test. A price literal
 * appearing anywhere in `src/` is the defect this rule exists to prevent.
 *
 * ── Only the WEB channel's rows, and only the ones with a price ─────────────────────────────
 * `/me/plans` carries every channel's store products: the App Store's, Google Play's and now the
 * web's. A store row has no `price` on it at all (the stores are the price authority for their own
 * catalogue) and a web row's price is null while the provider is unavailable. Both are dropped
 * rather than drawn as a card with no amount in it.
 *
 * ── The annual price is shown PER MONTH, and the saving is the real percentage ──────────────
 * The canon draws the annual option as the monthly equivalent with the yearly total under it, and
 * its badge carries the arithmetic rather than a round number: `1 - annual / (12 × monthly)`. Both
 * come out of the catalogue, so a price change at the provider changes both without a deploy.
 *
 * Pure: `t` arrives as a parameter, and the only clock or locale this reaches is the PRICE's own.
 */

import type { TFunction } from 'i18next';

import { numberFormat } from '~/shared/lib/intlCache';
import {
  TrainWithMeDataEnumsBillingPeriod as BillingPeriod,
  TrainWithMeDataEnumsSubscriptionChannel as Channel,
  type TrainWithMeDataEnumsBillingPeriod as BillingPeriodValue,
  type TrainWithMeModulesSubscriptionsDtosResponsesPlanCatalogItemResponse as PlanCatalogItem,
  type TrainWithMeModulesSubscriptionsDtosResponsesPlanStoreProductResponse as StoreProduct,
} from '~/shared/api/generated/model';

/**
 * The amount is written the way the invoice will be: Polish grouping, a comma before the grosze
 * and `zł` after them. It is NOT the reader's locale — R6 sells in PLN only, so an English-reading
 * trainer is still buying złoty and „PLN 44.99" would be a second spelling of one price.
 *
 * Trailing zero grosze are dropped (`998 zł`, not `998,00 zł`), which is how the canon writes the
 * yearly total and how every price on the landing is written.
 */
const PRICE_LOCALE = 'pl-PL';

/** The order the period switch draws its options in. Monthly first, as the canon has it. */
const PERIOD_ORDER: readonly BillingPeriodValue[] = [BillingPeriod.Monthly, BillingPeriod.Annual];

/** One price, as the card's three type sizes draw it. */
export interface PlanPriceFace {
  /** The big tabular number: „44,99 zł", „83,17 zł", „0 zł". */
  readonly amount: string;
  /** The bold line beside it: „zł / mies." or „na zawsze". */
  readonly unit: string;
  /** The line under that: „rozliczane miesięcznie", „998 zł raz w roku", or nothing at all. */
  readonly note: string | null;
}

/** One period this plan is sold on. */
export interface PlanPeriodOffer {
  readonly period: BillingPeriodValue;
  /** „Miesięcznie" / „Rocznie". */
  readonly label: string;
  /** „−48%", on the annual option and only when a monthly price exists to compare it against. */
  readonly saving: string | null;
  /** True on the option the launch promotion applies to — the annual one. */
  readonly promoted: boolean;
  readonly price: PlanPriceFace;
  /** The whole charge of one period, as the invoice writes it: „998 zł" a year, „159,99 zł" a month. */
  readonly total: string;
}

/** One card. */
export interface PlanOffer {
  readonly code: string;
  readonly name: string;
  /** The one-line tagline. Empty for a plan this app has no line for — the card then draws none. */
  readonly sub: string;
  /** „5 podopiecznych" / „Bez limitu podopiecznych". */
  readonly seats: string;
  /** The plan the account is on right now: flagged, muted, and its button refused. */
  readonly isCurrent: boolean;
  /** The one card wearing the ember edge and the screen's only ink primary. */
  readonly isRecommended: boolean;
  /** Every period this plan is sold on here. Empty when it is not for sale — the free plan. */
  readonly periods: readonly PlanPeriodOffer[];
  /** The period the card opens on: the annual one where it is sold, else the monthly one. */
  readonly defaultPeriod: BillingPeriodValue | null;
  /** The face drawn when there is nothing to choose — free's „0 zł · na zawsze". */
  readonly fixedPrice: PlanPriceFace | null;
}

export interface WebOffersInput {
  readonly plans: readonly PlanCatalogItem[];
  /** The plan the entitlement reports. A card matching it is the current one. */
  readonly currentPlanCode: string | null;
  /**
   * Whether the free level is offered to somebody who is not on it. True for a subscriber whose
   * channel manages the change (the portal or a store), where moving to Free is cancelling there;
   * false on the checkout route, where Free is nothing anybody can buy.
   */
  readonly offerFree?: boolean;
  readonly t: TFunction;
}

/**
 * An amount in minor units, as the invoice writes it. Exported because the subscription card says
 * what a trial will charge in the same spelling the picker used to sell it.
 */
export function formatPrice(amountMinor: number, currency: string): string {
  return numberFormat(PRICE_LOCALE, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amountMinor / 100);
}

/** A web row that actually carries a price, keyed by the period it bills on. */
function webPrices(
  products: readonly StoreProduct[],
): ReadonlyMap<BillingPeriodValue, { amountMinor: number; currency: string }> {
  const prices = new Map<BillingPeriodValue, { amountMinor: number; currency: string }>();
  for (const product of products) {
    if (product.channel !== Channel.Web) continue;
    const period = product.billingPeriod;
    const amountMinor = product.price?.amountMinor;
    const currency = product.price?.currency;
    if (period === undefined) continue;
    if (typeof amountMinor !== 'number' || amountMinor <= 0) continue;
    if (typeof currency !== 'string' || currency === '') continue;
    // First row wins. Two web rows for one period is a catalogue defect, not a choice to make here.
    if (prices.has(period)) continue;
    prices.set(period, { amountMinor, currency });
  }
  return prices;
}

/**
 * The saving, as a whole percentage of the twelve monthly payments the annual one replaces.
 *
 * `null` when either side is missing: a plan sold annually and not monthly has nothing to be
 * cheaper than, and a badge reading „−0%" is worse than no badge.
 */
function savingPercent(monthlyMinor: number | undefined, annualMinor: number): number | null {
  if (monthlyMinor === undefined || monthlyMinor <= 0) return null;
  const percent = Math.round((1 - annualMinor / (12 * monthlyMinor)) * 100);
  return percent > 0 ? percent : null;
}

function seatLine(activeClientLimit: number | null, t: TFunction): string {
  // No cap is the unlimited plan. Otherwise one seat is always the trainer's own (backend ADR
  // 2026-08-12), so a cap of six is five people coached — the same arithmetic the plate does.
  if (activeClientLimit === null) return t('web:account.subscription.picker.seatsUnlimited');
  return t('web:account.subscription.picker.seats', {
    count: Math.max(0, activeClientLimit - 1),
  });
}

function periodOffer(
  period: BillingPeriodValue,
  price: { amountMinor: number; currency: string },
  monthlyMinor: number | undefined,
  t: TFunction,
): PlanPeriodOffer {
  const annual = period === BillingPeriod.Annual;
  // The annual price is drawn per month, rounded to whole grosze, with the year's total under it.
  const shownMinor = annual ? Math.round(price.amountMinor / 12) : price.amountMinor;
  const percent = annual ? savingPercent(monthlyMinor, price.amountMinor) : null;

  return {
    period,
    label: t(
      annual
        ? 'web:account.subscription.picker.annual'
        : 'web:account.subscription.picker.monthly',
    ),
    saving: percent === null ? null : t('web:account.subscription.picker.saving', { percent }),
    // The launch offer is the yearly commitment's, which is what the canon draws the line under.
    promoted: annual,
    total: formatPrice(price.amountMinor, price.currency),
    price: {
      amount: formatPrice(shownMinor, price.currency),
      unit: t('web:account.subscription.picker.perMonth'),
      note: annual
        ? t('web:account.subscription.picker.billedAnnually', {
            total: formatPrice(price.amountMinor, price.currency),
          })
        : t('web:account.subscription.picker.billedMonthly'),
    },
  };
}

/**
 * The cards the picker draws, in the catalogue's own order.
 *
 * A plan is drawn when it is the one the account is on, or when it can actually be bought here.
 * A plan that is neither — a level the web does not sell to a trainer who is not on it — is not a
 * card with a missing button; it is not an offer at all.
 *
 * The RECOMMENDED card is the last purchasable one in the catalogue's order rather than a plan
 * code written down here. The catalogue is sorted by level, so that is the top of the range, and a
 * fourth level added upstream moves the flag without a deploy on this side.
 */
export function webOffers({ plans, currentPlanCode, offerFree = false, t }: WebOffersInput): readonly PlanOffer[] {
  const ordered = [...plans].sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0));

  const offers: PlanOffer[] = [];
  for (const plan of ordered) {
    const code = plan.code ?? '';
    if (code === '') continue;

    const prices = webPrices(plan.storeProducts ?? []);
    const monthlyMinor = prices.get(BillingPeriod.Monthly)?.amountMinor;
    const periods = PERIOD_ORDER.flatMap((period) => {
      const price = prices.get(period);
      return price === undefined ? [] : [periodOffer(period, price, monthlyMinor, t)];
    });

    const isCurrent = currentPlanCode !== null && code === currentPlanCode;
    if (periods.length === 0 && !isCurrent && !(offerFree && plan.isFree === true)) continue;

    offers.push({
      code,
      name: plan.name ?? code,
      // `defaultValue` rather than the key: a level this app has written no line for draws no line,
      // and „web:account.subscription.picker.sub.enterprise" on a card is worse than silence.
      sub: t(`web:account.subscription.picker.sub.${code}`, { defaultValue: '' }),
      seats: seatLine(plan.activeClientLimit ?? null, t),
      isCurrent,
      isRecommended: false,
      periods,
      defaultPeriod:
        periods.find((option) => option.period === BillingPeriod.Annual)?.period ??
        periods[0]?.period ??
        null,
      fixedPrice:
        periods.length > 0
          ? null
          : {
              amount: t('web:account.subscription.picker.freeAmount'),
              unit: t('web:account.subscription.picker.freeUnit'),
              note: null,
            },
    });
  }

  const recommended = offers.findLast((offer) => offer.periods.length > 0);
  return offers.map((offer) => (offer === recommended ? { ...offer, isRecommended: true } : offer));
}

/** Everything a card's price block draws once a period is chosen. */
export interface PlanFace {
  readonly price: PlanPriceFace;
  /** Whether the launch-offer line belongs under this face. */
  readonly promoted: boolean;
}

/**
 * What a card is showing right now: the chosen period's face, or the fixed one where there is
 * nothing to choose.
 *
 * One entry point rather than a `find` plus a fallback at the call site, because the fallback IS a
 * decision — a card whose stored period is no longer sold falls back to the one it opens on rather
 * than drawing an empty price row.
 */
export function planFace(offer: PlanOffer, period: BillingPeriodValue | null): PlanFace | null {
  if (offer.periods.length === 0) {
    return offer.fixedPrice === null ? null : { price: offer.fixedPrice, promoted: false };
  }
  const chosen =
    offer.periods.find((option) => option.period === period) ??
    offer.periods.find((option) => option.period === offer.defaultPeriod);
  return chosen === undefined ? null : { price: chosen.price, promoted: chosen.promoted };
}

/**
 * Where pressing a plan card leads, which is the CHANNEL's answer and never the card's.
 *
 * The cards stand on this tab for every trainer, because a subscriber who cannot see the other
 * levels has no way to find out that changing them is possible (owner, 23 September 2026). What
 * a press does is still requirement R4's: the channel that sold a subscription is the only one that
 * changes it.
 *
 * · `checkout` — nothing is billing this account, or only a comped grant is. The provider's payment
 *   page; R11's rule that a comp may only be exceeded is the backend's refusal, already worded.
 * · `portal` — a web subscription. The provider's portal does the change on R8's terms.
 * · `store` — a store subscription. The store's own settings; this page fakes no control it lacks.
 *
 * A `TestStore` subscription is the one channel with no page to send anybody to, so it takes the
 * checkout route and the backend's `storeLiveElsewhere` refusal says where it is managed.
 */
export type PlanChangeRoute = 'checkout' | 'portal' | 'store';

export function planChangeRoute(entitlements: {
  readonly isFree?: boolean;
  readonly channel?: string | null;
}): PlanChangeRoute {
  if (entitlements.isFree === true) return 'checkout';
  switch (entitlements.channel) {
    case Channel.Web:
      return 'portal';
    case Channel.AppStore:
    case Channel.GooglePlay:
      return 'store';
    default:
      return 'checkout';
  }
}
